import React, { useState } from "react";
import { FeatherRefreshCw } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { SyncReportPanel } from "./SyncReportPanel";
import { TallySyncReport } from "../types/tallySyncReport";

function formatLastSynced(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value;
  }
  return d.toLocaleString();
}

interface SyncModuleCardProps {
  title: string;
  description: string;
  syncLabel?: string;
  /** When false, hides dry-run control (default true). */
  showDryRun?: boolean;
  /** Disable sync actions (e.g. while waiting for a later phase). */
  disabled?: boolean;
  /** Shown under the description (Phase 5). */
  lastSyncedAt?: string | null;
  /** Extra note for long-running syncs (e.g. GL). */
  longRunningHint?: string;
  onSync: (dryRun: boolean) => Promise<TallySyncReport>;
  /** Called after a successful sync (implemented + success). */
  onSuccess?: (report: TallySyncReport) => void;
}

export const SyncModuleCard: React.FC<SyncModuleCardProps> = ({
  title,
  description,
  syncLabel = "Sync",
  showDryRun = true,
  disabled = false,
  lastSyncedAt = null,
  longRunningHint,
  onSync,
  onSuccess,
}) => {
  const [dryRun, setDryRun] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState<TallySyncReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setReport(null);
    try {
      const result = await onSync(showDryRun ? dryRun : false);
      setReport(result);
      if (
        result.success &&
        result.meta?.implemented !== false &&
        !result.meta?.dry_run
      ) {
        onSuccess?.(result);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
      <div className="flex w-full items-start justify-between gap-4">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">
            {title}
          </span>
          <span className="text-body font-body text-subtext-color">
            {description}
          </span>
          {longRunningHint ? (
            <span className="text-caption font-caption text-subtext-color">
              {longRunningHint}
            </span>
          ) : null}
          {formatLastSynced(lastSyncedAt) ? (
            <span className="text-caption font-caption text-subtext-color">
              Last synced: {formatLastSynced(lastSyncedAt)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          {showDryRun ? (
            <label className="flex items-center gap-2 text-caption font-caption text-subtext-color whitespace-nowrap">
              <input
                type="checkbox"
                checked={dryRun}
                disabled={disabled || syncing}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry-run
            </label>
          ) : null}
          <Button
            variant="brand-primary"
            icon={<FeatherRefreshCw />}
            onClick={handleSync}
            loading={syncing}
            disabled={disabled || syncing}
          >
            {syncing ? "Syncing..." : syncLabel}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="flex w-full flex-col items-start gap-1 rounded-lg border border-solid border-red-200 bg-red-50 p-4">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      ) : null}

      {report ? <SyncReportPanel report={report} /> : null}
    </div>
  );
};
